import React, { useState } from "react";
import styles from "./invoice_supplier_section.module.css";
import {
    ActionButton,
    Content,
    Dialog,
    DialogContainer,
    Flex,
    Heading,
    Link,
    ProgressCircle,
    TextField,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import SupplierDroDown from "./formComponents/SupplierDropDown";
import { AIChatIcon } from "../../../../../components/protected/Icons/IconsLib";
import { Button } from "react-bootstrap";
import UserDropDown from "./formComponents/UserDropDown";
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { GetContactRequest } from "../../../../../services/soap/main/requests/GetContactRequest";

interface IInvoiceSupplierSection {
    invoice: any;
    isEdit: any;
    onChange: (data: any) => void;
}

const InvoiceSupplierSection: React.FC<IInvoiceSupplierSection> = ({
    invoice,
    isEdit,
    onChange,
}) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);
    const { addressTypes } = usePreloadAssets();
    const { store, mainService } = useDependency();

    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
    const _tt = (key: string) => t(`invoice_common.${key}`, { ns: "invoice" });

    const handleShowMorefields = () => {
        setShowMorefields(!showMorefileds);
    };

    const normalizeAddresses = (addressesWrapper: any): any[] => {
        if (!addressesWrapper) return [];

        let raw = addressesWrapper;

        // ADDRESSES: { ADDRESS: ... }
        if (typeof raw === "object" && !Array.isArray(raw) && raw.ADDRESS) {
            raw = raw.ADDRESS;
        }

        // raw can be array or single object
        const list = Array.isArray(raw) ? raw : [raw];

        // each element can be { ADDRESS: {...} } or direct {...}
        return list.map((x) => x?.ADDRESS ?? x).filter(Boolean);
    };

    const pickAddress = (
        entityType: "COMPANY" | "PERSON",
        addressesWrapper: any
    ) => {
        const list = normalizeAddresses(addressesWrapper);
        if (!list.length) return null;

        const isPrimary = (a: any) => String(a?.isPrimary) === "1";

        const matchByAddressTypes = (a: any) => {
            const typeId = String(a?.type ?? "");
            const at = (addressTypes as any)?.[typeId];
            if (!at) return false;

            // Company: invoiceAddress=1, Person: permanentAddress=1
            return entityType === "COMPANY"
                ? String(at?.invoiceAddress) === "1"
                : String(at?.permanentAddress) === "1";
        };

        // 1) exact match by addressTypes
        const exact = list.find(matchByAddressTypes);
        if (exact) return exact;

        // 2) primary
        const primary = list.find(isPrimary);
        if (primary) return primary;

        // 3) index 0
        return list[0];
    };

    const handleSupplierChange = async (data: any) => {
        if (!data) {
            onChange({
                ...invoice,
                provider: "",
                providerName: "",
                providerStreet: "",
                providerCity: "",
                providerZIP: "",
                providerRegion: "",
                providerCountry: "",
                providerIssuedBy: "",
                providerIssuedByName: "",
            });
            return;
        }

        const response: any = await mainService.getContact(
            new GetContactRequest(
                store.Server,
                store.SessionId,
                undefined,
                undefined,
                undefined,
                data.id
            )
        );

        if (response?.result !== "OK") return;

        const isPerson = !!response?.PERSON;
        const entity = isPerson ? response.PERSON : response.COMPANY;
        const entityType: "PERSON" | "COMPANY" = isPerson ? "PERSON" : "COMPANY";

        const address = pickAddress(entityType, entity?.ADDRESSES);

        onChange({
            ...invoice,
            provider: data.id,
            providerName: data?.name ?? entity?.name1 ?? entity?.name ?? "",

            providerStreet: address?.street ?? "",
            providerCity: address?.cityName ?? address?.city ?? "",
            providerZIP: address?.zipName ?? address?.zip ?? "",
            providerRegion: address?.regionName ?? address?.region ?? "",
            providerCountry: address?.countryName ?? address?.country ?? "",
        });
    };

    function SupplierModal() {
        return (
            <>
                <Dialog
                    width={{ L: "800px", base: "100%" }}
                    aria-label="Add Product"
                    isDismissable
                    onDismiss={() => setShowMorefields(false)}
                >
                    <Heading level={2}>{_t(isEdit ? "supplier" : "supplier")}</Heading>
                    <Content>
                        <Flex
                            direction={{ base: "column", L: "row" }}
                            gap={"size-150"}
                            marginTop={"size-150"}
                        >
                            <SupplierDroDown
                                selectedSupplier={invoice?.provider}
                                supplierName={invoice?.providerName}
                                isEdit={isEdit}
                                onChange={(data) => {
                                    handleSupplierChange(data);
                                }}
                            />
                            <TextField
                                width={"100%"}
                                label={_t("street")}
                                value={invoice?.providerStreet}
                                onChange={(e: any) => onChange({ ...invoice, providerStreet: e })}
                            />
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <UserDropDown
                                width={{ base: "100%", L: "52%" }}
                                label="issued_by"
                                isEdit={isEdit}
                                selectedUser={invoice?.providerIssuedBy}
                                onChange={(data) => {
                                    onChange({
                                        ...invoice,
                                        providerIssuedBy: data != null ? data.id : "",
                                        providerIssuedByName: data != null ? data.name : "",
                                    });
                                }}
                            />
                            <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                                <TextField
                                    width={{ base: "100%", L: "60" }}
                                    label="City"
                                    value={invoice?.providerCity}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, providerCity: e })
                                    }
                                />
                                <TextField
                                    width={{ base: "100%", L: "40" }}
                                    label="Zip Code"
                                    value={invoice?.providerZIP}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, providerZIP: e })
                                    }
                                />
                            </Flex>
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <TextField
                                width={{ base: "100%", L: "52%" }}
                                label="Name"
                                value={invoice?.providerName}
                                onChange={(e: any) => onChange({ ...invoice, providerName: e })}
                            />
                            <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                                <TextField
                                    width={{ base: "100%", L: "60" }}
                                    label="Region"
                                    value={invoice?.providerRegion}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, providerRegion: e })
                                    }
                                />
                                <TextField
                                    width={{ base: "100%", L: "40" }}
                                    label="Country"
                                    value={invoice?.providerCountry}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, providerCountry: e })
                                    }
                                />
                            </Flex>
                        </Flex>
                    </Content>
                </Dialog>

                <Flex
                    UNSAFE_style={{
                        backgroundColor: "white",
                        justifyContent: "end",
                        width: "100%",
                        padding: "1rem",
                        paddingLeft: 55,
                        paddingRight: 50,
                        boxShadow: "0 -1px 1px rgba(0,0,0,0.1)",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 1,
                    }}
                >
                    <Flex gap={"size-200"} UNSAFE_className={styles.button_container}>
                        <Button
                            variant="danger"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_cancel}`}
                        >
                            {_tt("button_cancel")}
                        </Button>
                        <Button
                            type="submit"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_add}`}
                        >
                            {_tt("button_ok")}
                        </Button>
                    </Flex>
                </Flex>
            </>
        );
    }

    return (
        <Flex
            direction={{ base: "column", L: "column" }}
            UNSAFE_className={styles.main_container}
        >
            <Flex justifyContent={"end"}>
                <Link
                    UNSAFE_className={styles.show_more_button}
                    onPress={handleShowMorefields}
                    isQuiet
                >
                    Show more
                </Link>
            </Flex>

            <SupplierDroDown
                selectedSupplier={invoice?.provider}
                supplierName={invoice?.providerName}
                isEdit={isEdit}
                onChange={(data) => {
                    handleSupplierChange(data);
                }}
            />

            <DialogContainer onDismiss={() => setShowMorefields(false)}>
                {showMorefileds && SupplierModal()}
            </DialogContainer>
        </Flex>
    );
};

export default InvoiceSupplierSection;
